/** Offline host dashboard demo — fixtures + local mutations (no API). */

import { activateDemoSession, getTodaysDemoEvent } from "./demoExperience";
import { demoProfileThumbnailUrl } from "./demoProfileThumbnails";

export const HOST_DEMO_EVENT_ID = "demo-host-today";
export const HOST_DEMO_ONBOARDING_KEY = "ln_host_demo_onboarding_v1";

const HOST_FIXTURE_GUESTS = [
  { dancer_id: "demo-guest-carla", name: "Carla", role: "Follower", dance_forms: [{ name: "Salsa", level: "Intermediate" }], first_timer: false, hide_name: false, attendance_status: "likely", phone: "+49 170 1234567", thumbnail_url: demoProfileThumbnailUrl("carla") },
  { dancer_id: "demo-guest-marco", name: "Marco", role: "Leader", dance_forms: [{ name: "Salsa", level: "Advanced" }], first_timer: false, hide_name: false, attendance_status: "likely", phone: "+49 170 7654321", thumbnail_url: demoProfileThumbnailUrl("marco") },
  { dancer_id: "demo-guest-lina", name: "Lina", role: "Follower", dance_forms: [{ name: "Bachata", level: "Beginner" }], first_timer: true, hide_name: false, attendance_status: "likely", phone: "+49 170 2223344", thumbnail_url: demoProfileThumbnailUrl("lina") },
  { dancer_id: "demo-guest-tomas", name: "Tomas", role: "Leader", dance_forms: [{ name: "Salsa", level: "Improver" }], first_timer: false, hide_name: false, attendance_status: "likely", phone: "+49 170 3334455", thumbnail_url: demoProfileThumbnailUrl("tomas") },
  { dancer_id: "demo-guest-yuki", name: "Yuki", role: "Both", dance_forms: [{ name: "Kizomba", level: "Intermediate" }], first_timer: false, hide_name: false, attendance_status: "likely", phone: "+49 170 5556677", thumbnail_url: demoProfileThumbnailUrl("yuki") },
  { dancer_id: "demo-guest-sofia", name: "Sofia", role: "Follower", dance_forms: [{ name: "Salsa", level: "Advanced" }], first_timer: false, hide_name: true, attendance_status: "likely", phone: "+49 170 6667788", thumbnail_url: demoProfileThumbnailUrl("sofia") },
  { dancer_id: "demo-guest-jamal", name: "Jamal", role: "Leader", dance_forms: [{ name: "Bachata", level: "Intermediate" }], first_timer: false, hide_name: false, attendance_status: "likely", phone: "+49 170 7778899", thumbnail_url: demoProfileThumbnailUrl("jamal") },
  { dancer_id: "demo-guest-elena", name: "Elena", role: "Follower", dance_forms: [{ name: "Salsa", level: "Improver" }], first_timer: false, hide_name: false, attendance_status: "likely", phone: "+49 170 8889900", thumbnail_url: demoProfileThumbnailUrl("elena") },
  { dancer_id: "demo-guest-ravi", name: "Ravi", role: "Leader", dance_forms: [{ name: "Salsa", level: "Beginner" }], first_timer: true, hide_name: false, attendance_status: "maybe", phone: "+49 170 9990011", thumbnail_url: demoProfileThumbnailUrl("ravi") },
  { dancer_id: "demo-guest-naomi", name: "Naomi", role: "Follower", dance_forms: [{ name: "Bachata", level: "Intermediate" }], first_timer: false, hide_name: false, attendance_status: "not_coming", phone: "+49 170 0001122", thumbnail_url: demoProfileThumbnailUrl("naomi") },
];

const INVITE_REQUESTS = [
  {
    id: "demo-invite-1",
    dancer_id: "demo-guest-pending-1",
    name: "Helena",
    phone: "+49 170 1010101",
    role: "Follower",
    message: "Friend of Carla — first time at your social!",
    thumbnail_url: demoProfileThumbnailUrl("helena"),
  },
  {
    id: "demo-invite-2",
    dancer_id: "demo-guest-pending-2",
    name: "Andre",
    phone: "+49 170 2020202",
    role: "Leader",
    message: null,
    thumbnail_url: demoProfileThumbnailUrl("andre"),
  },
];

function randomId(n = 8) {
  const bytes = crypto.getRandomValues(new Uint8Array(n));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("").slice(0, n);
}

function buildRsvps(guests) {
  return guests.map((g) => ({
    id: g.dancer_id,
    dancer_id: g.dancer_id,
    name: g.name,
    role: g.role,
    dance_forms: g.dance_forms,
    first_timer: g.first_timer || false,
    hide_name: g.hide_name || false,
    attendance_status: g.attendance_status || "likely",
    phone: g.phone || "",
    thumbnail_url: g.thumbnail_url,
    checked_in_at: null,
  }));
}

function computeCounts(rsvps) {
  const coming = rsvps.filter((r) => r.attendance_status === "likely" || r.attendance_status === "maybe");
  let leaders = 0;
  let followers = 0;
  let both = 0;
  for (const r of coming.filter((r) => r.attendance_status === "likely")) {
    if (r.role === "Leader") leaders += 1;
    else if (r.role === "Follower") followers += 1;
    else if (r.role === "Both") both += 1;
  }
  const bothHalf = both / 2;
  return {
    total: coming.length,
    balanced_leaders: leaders + bothHalf,
    balanced_followers: followers + bothHalf,
    both,
  };
}

function weeklyOccurrences(now = new Date()) {
  const base = new Date(now);
  base.setHours(21, 0, 0, 0);
  const day = base.getDay();
  const diffToFriday = (5 - day + 7) % 7;
  base.setDate(base.getDate() + diffToFriday - 7);
  const dates = [-7, 0, 7].map((offset) => {
    const d = new Date(base);
    d.setDate(d.getDate() + offset);
    return d.toISOString();
  });
  return dates;
}

function buildDashboardEvent(now = new Date()) {
  const template = getTodaysDemoEvent(now);
  const occurrences = weeklyOccurrences(now);
  const selectedOccurrence = occurrences[1].slice(0, 10);
  const rsvps = buildRsvps(HOST_FIXTURE_GUESTS);
  const counts = computeCounts(rsvps);

  return {
    id: HOST_DEMO_EVENT_ID,
    name: template.name,
    description: template.description,
    location: template.location,
    city: template.city || "Berlin",
    city_slug: template.city_slug || "berlin",
    public_slug: "demo-host-night",
    public_path: "/demo/host",
    starts_at: occurrences[1],
    door_time: null,
    occurrence_date: selectedOccurrence,
    selected_occurrence: selectedOccurrence,
    cover_color: template.cover_color,
    pricing: template.pricing,
    organizer_name: template.organizer_name,
    is_live: true,
    is_demo: true,
    lifecycle: "published",
    recurring: "weekly",
    recurring_end: null,
    access: "invite-only",
    requires_invite: true,
    capacity: 80,
    leader_cap: 40,
    follower_cap: 40,
    event_status: "confirmed",
    status_reason: null,
    occurrence_overrides: {},
    occurrences,
    rsvps,
    invite_requests: [...INVITE_REQUESTS],
    waitlisted: 2,
    balance_message: "Role ratio balanced. 3 more leaders recommended.",
    counts,
  };
}

function buildPastAttendees() {
  return [
    { dancer_id: "demo-past-1", name: "Pablo", role: "Leader", last_occurrence: "2026-06-27", phone: "+49 170 1112233", whatsapp_url: "https://wa.me/491701112233" },
    { dancer_id: "demo-past-2", name: "Mei", role: "Follower", last_occurrence: "2026-06-27", phone: "+49 170 4445566", whatsapp_url: "https://wa.me/491704445566" },
    { dancer_id: "demo-past-3", name: "Dan", role: "Both", last_occurrence: "2026-06-20", phone: "+49 170 7778899", whatsapp_url: "https://wa.me/491707778899" },
  ];
}

function buildUpdateSubscribers() {
  return [
    { dancer_id: "demo-sub-1", name: "Carla", phone: "+49 170 1234567", on_profile: true, whatsapp_url: "https://wa.me/491701234567" },
    { dancer_id: "demo-sub-2", name: "Marco", phone: "+49 170 7654321", on_profile: false, whatsapp_url: "https://wa.me/491707654321" },
  ];
}

function buildFavouriteInsights() {
  return {
    summary: { favourite_count: 12 },
    repeat_without_favourite: [
      { dancer_id: "demo-repeat-1", name: "Tomas", visit_count: 4, last_occurrence: "2026-06-27", phone: "+49 170 3334455", whatsapp_url: "https://wa.me/491703334455" },
      { dancer_id: "demo-repeat-2", name: "Sofia", visit_count: 3, last_occurrence: "2026-06-20", phone: "+49 170 6667788", whatsapp_url: "https://wa.me/491706667788" },
    ],
  };
}

function buildBroadcasts(now = new Date()) {
  const hourAgo = new Date(now.getTime() - 60 * 60 * 1000).toISOString();
  const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
  return [
    { id: "demo-broadcast-1", message: "Doors open 30 minutes early tonight — beginners' class in the small room!", created_at: hourAgo, recalled_at: null },
    { id: "demo-broadcast-2", message: "Bring cash for the bar — card not accepted.", created_at: dayAgo, recalled_at: dayAgo },
  ];
}

export function getDemoHostDashboardState(now = new Date()) {
  return {
    dashboard: buildDashboardEvent(now),
    pastAttendees: buildPastAttendees(),
    updateSubscribers: buildUpdateSubscribers(),
    favouriteInsights: buildFavouriteInsights(),
    broadcasts: buildBroadcasts(now),
  };
}

export function isHostDemoOnboardingComplete() {
  try {
    return localStorage.getItem(HOST_DEMO_ONBOARDING_KEY) === "1";
  } catch {
    return false;
  }
}

export function markHostDemoOnboardingComplete() {
  try {
    localStorage.setItem(HOST_DEMO_ONBOARDING_KEY, "1");
  } catch {
    /* private browsing */
  }
}

export function clearHostDemoOnboarding() {
  try {
    localStorage.removeItem(HOST_DEMO_ONBOARDING_KEY);
  } catch {
    /* private browsing */
  }
}

export function resetHostDemo() {
  clearHostDemoOnboarding();
  activateDemoSession(true);
  if (typeof window !== "undefined") {
    window.location.assign("/demo/host?demo=true");
  }
}

export function applyDemoHostMutation(state, action) {
  const next = { ...state, dashboard: { ...state.dashboard } };

  switch (action.type) {
    case "remove_rsvp": {
      const dancerId = action.dancerId;
      next.dashboard.rsvps = next.dashboard.rsvps.filter((r) => r.dancer_id !== dancerId);
      next.dashboard.counts = computeCounts(next.dashboard.rsvps);
      return next;
    }
    case "invite_action": {
      const { requestId, inviteAction } = action;
      const request = next.dashboard.invite_requests.find((r) => r.id === requestId);
      if (!request) return state;
      next.dashboard.invite_requests = next.dashboard.invite_requests.filter((r) => r.id !== requestId);
      if (inviteAction === "accept") {
        next.dashboard.rsvps = [
          ...next.dashboard.rsvps,
          {
            id: request.dancer_id,
            dancer_id: request.dancer_id,
            name: request.name,
            role: request.role,
            dance_forms: [],
            first_timer: false,
            hide_name: false,
            attendance_status: "likely",
            thumbnail_url: request.thumbnail_url,
            checked_in_at: null,
          },
        ];
        next.dashboard.counts = computeCounts(next.dashboard.rsvps);
      }
      return next;
    }
    case "save_amendment": {
      const { payload, occurrenceDate } = action;
      if (payload.description_append) {
        const overrides = { ...next.dashboard.occurrence_overrides };
        const existing = overrides[occurrenceDate] || {};
        overrides[occurrenceDate] = {
          ...existing,
          description_append: payload.description_append,
        };
        next.dashboard.occurrence_overrides = overrides;
      }
      if (payload.starts_at) next.dashboard.starts_at = payload.starts_at;
      if (payload.door_time !== undefined) next.dashboard.door_time = payload.door_time;
      if (payload.status) {
        next.dashboard.event_status = payload.status;
        next.dashboard.status_reason = payload.status_reason || null;
      }
      return next;
    }
    case "lifecycle": {
      const { lifecycleAction, reason } = action;
      if (lifecycleAction === "cancel") {
        next.dashboard.event_status = "cancelled";
        next.dashboard.status_reason = reason || "Cancelled";
      } else if (lifecycleAction === "restore") {
        next.dashboard.event_status = "confirmed";
        next.dashboard.status_reason = null;
      } else if (lifecycleAction === "archive") {
        next.dashboard.lifecycle = "archived";
      } else if (lifecycleAction === "unarchive") {
        next.dashboard.lifecycle = "published";
      } else if (lifecycleAction === "delete") {
        return getDemoHostDashboardState();
      }
      return next;
    }
    case "broadcast_send": {
      const broadcast = {
        id: `demo-broadcast-${randomId(6)}`,
        message: action.message,
        created_at: new Date().toISOString(),
        recalled_at: null,
      };
      next.broadcasts = [broadcast, ...next.broadcasts];
      return next;
    }
    case "broadcast_recall": {
      if (action.mode === "all") {
        next.broadcasts = next.broadcasts.map((b) =>
          b.recalled_at ? b : { ...b, recalled_at: new Date().toISOString() },
        );
      } else {
        const idx = next.broadcasts.findIndex((b) => !b.recalled_at);
        if (idx >= 0) {
          const updated = [...next.broadcasts];
          updated[idx] = { ...updated[idx], recalled_at: new Date().toISOString() };
          next.broadcasts = updated;
        }
      }
      return next;
    }
    case "change_occurrence": {
      next.dashboard.selected_occurrence = action.occurrenceDate;
      return next;
    }
    default:
      return state;
  }
}

export function buildDemoGuestListCsv(rsvps, eventName = "demo-event") {
  const headers = ["name", "phone", "role", "attendance", "dance_forms", "first_timer", "hide_name"];
  const rows = rsvps.map((r) => [
    r.name,
    r.phone || "",
    r.role || "",
    r.attendance_status || "likely",
    (r.dance_forms || []).map((d) => `${d.name} (${d.level})`).join("; "),
    r.first_timer ? "yes" : "no",
    r.hide_name ? "yes" : "no",
  ]);
  const escape = (v) => {
    const s = String(v ?? "");
    return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = `\uFEFF${[headers.join(","), ...rows.map((row) => row.map(escape).join(","))].join("\r\n")}\r\n`;
  const filename = `${eventName.replace(/[^a-zA-Z0-9-_]+/g, "-")}-guest-list.csv`;
  return { csv, filename, rowCount: rows.length };
}

export function downloadDemoGuestListCsv(rsvps, eventName) {
  const { csv, filename, rowCount } = buildDemoGuestListCsv(rsvps, eventName);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return rowCount;
}
